import { PageHeaderWrapper } from "@clipboard-health/ui-components";
import { isDefined, isNil } from "@clipboard-health/util-ts";
import { useGetClipboardScoreHistory } from "@src/appV2/ClipboardScore/api/useGetClipboardScoreHistory";
import { useDynamicPenaltiesConfig } from "@src/appV2/ClipboardScore/hooks/featureFlags";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { useArrivalTimeConfig } from "@src/appV2/redesign/ArrivalTime/useArrivalTimeConfig";
import { useDefinedWorker } from "@src/appV2/Worker/useDefinedWorker";

import { ListLoadingState } from "../../../components/ListLoadingState";
import { PageWrapper } from "../../../components/PageWrapper";
import { PageContent } from "../../../WorkerShifts/components/PageContent";
import { useShowZeroScoreLogs } from "../../hooks/featureFlags";
import { ClipboardScoreError } from "../ClipboardScoreError";
import { ClipboardScoreHistoryContent } from "./Content";
import { ClipboardScoreHistoryHeader } from "./Header";

const NUMBER_OF_SCORE_LOGS = 60;

export function ClipboardScoreHistory() {
  const { userId } = useDefinedWorker();
  const {
    data: clipboardScoreHistory,
    isLoading: isClipboardScoreHistoryLoading,
    refetch: refetchClipboardScoreHistory,
    isError: isClipboardScoreHistoryError,
    error: clipboardScoreHistoryError,
  } = useGetClipboardScoreHistory(
    { userId, pageSize: NUMBER_OF_SCORE_LOGS },
    { enabled: isDefined(userId) }
  );
  const { enabled: isDynamicPenaltiesEnabled } = useDynamicPenaltiesConfig();
  const { useExpectedArrivalTime } = useArrivalTimeConfig();
  const showZeroScoreLogs = useShowZeroScoreLogs();

  if (isClipboardScoreHistoryLoading) {
    return <ListLoadingState texts={["Loading score logs..."]} />;
  }

  if (isClipboardScoreHistoryError || isNil(clipboardScoreHistory)) {
    logError(APP_V2_APP_EVENTS.CLIPBOARD_SCORE_HISTORY_ERROR, {
      error: clipboardScoreHistoryError,
      metadata: { workerId: userId },
    });
    return (
      <ClipboardScoreError
        title="Error loading clipboard score history"
        onRefresh={refetchClipboardScoreHistory}
      />
    );
  }

  const scoreLogsToShow = showZeroScoreLogs
    ? clipboardScoreHistory.scoreLogs
    : clipboardScoreHistory.scoreLogs.filter((log) => log.clipboardScoreAdjustment !== 0);

  logEvent(APP_V2_USER_EVENTS.CLIPBOARD_SCORE_HISTORY_VIEWED, {
    workerId: userId,
  });

  return (
    <PageWrapper variant="tertiary">
      <PageHeaderWrapper variant="tertiary">
        <ClipboardScoreHistoryHeader />
      </PageHeaderWrapper>
      <PageContent>
        <ClipboardScoreHistoryContent
          logs={scoreLogsToShow}
          isDynamicPenaltiesEnabled={isDynamicPenaltiesEnabled}
          useExpectedArrivalTime={useExpectedArrivalTime}
        />
      </PageContent>
    </PageWrapper>
  );
}
